import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'
import LinearProgress from '@components/progress/LinearProgress'
import SideDrawer from '@components/drawer/SideDrawer'
import Drawer from '@components/drawer/FixedDrawer'
import Tag from '@components/tag/Tag'

import Response from './Response'

import { FormikHelpers, useFormik } from 'formik'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as QuestionStore from '@store/question/question'
import * as MetierStore from '@store/metier/metier'

import { filterInTree, findAllAuditable } from '@screens/controle/form/utils'
import { findLocalisationParents } from '@screens/incident/utils'

interface Values {
  reponses: ControleResponseForm[]
}

const ITEM_WIDTH = 360

const Questions = ({ form, onForm, onQuit }: ControleFormProps) => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()

  const init: Values = React.useMemo(() => ({ reponses: form.reponses || [] }), [form.reponses])

  const [opens, setOpens] = React.useState<string[]>([])

  const services = useReducer(SocieteStore.store, (s) => s.services)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const controleQuestions = useReducer(QuestionStore.store, (s) => s.controle)
  const questions = React.useMemo(
    () => controleQuestions.filter((q) => q.metierId === form.metierId),
    [controleQuestions, form.metierId]
  )

  const places = useReducer(LocationStore.store, (s) => s.places)
  const locationTree = useReducer(LocationStore.store, (s) => s.tree)
  const selectedLocationTree = React.useMemo(
    () => locationTree.filter((item) => filterInTree(item, form.localisationIds)),
    [form.localisationIds]
  )

  const allAuditable = React.useMemo(
    () => selectedLocationTree.flatMap((tree) => findAllAuditable(tree, form.localisationIds)),
    [selectedLocationTree, form.localisationIds]
  )

  const notAudited = React.useMemo(
    () => allAuditable.filter((id) => form.reponses.filter((r) => r.localisationId === id).length !== questions.length),
    [allAuditable, form.reponses, questions]
  )

  const societe = React.useMemo(() => services.find((s) => s.id === form.societeId), [services])

  const metier = React.useMemo(() => metiers.find((m) => m.id === form.metierId), [metiers])

  const manif = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (m) => m.id === form.manifestationId
      ),
    [manifestations]
  )

  const title = React.useMemo(
    () => [societe ? societe.nameSociete : '', metier ? metier.libelle : ''].filter(Boolean).join(' - '),
    [societe, metier]
  )

  const subtitle = React.useMemo(
    () =>
      [manif ? manif.libelle : '', form.period ? i18n.t(`screens.controles.form.period.${form.period}`) : undefined]
        .filter(Boolean)
        .join(' - '),
    [manif, form.period]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  React.useEffect(() => {
    const completed = allAuditable.length * questions.length === (form.reponses || []).length
    onForm({ completed })
  }, [allAuditable.length, form.reponses])

  const onOpen = (id: string, open: boolean) => {
    if (open) {
      setOpens(opens.filter((o) => o !== id))
    } else {
      setOpens([...opens, id])
    }
  }

  const onReponse = (id: string, vals: Values) => {
    if (form.answerMode === 'allSpaces') {
      setFieldValue(
        'reponses',
        form.localisationIds.reduce(
          (acc, cur) => [...acc, ...vals.reponses.map((r) => ({ ...r, localisationId: cur }))],
          [] as ControleResponseForm[]
        )
      )
    } else {
      const removed = values.reponses.filter((r) => r.localisationId !== id)
      setFieldValue('reponses', [...removed, ...vals.reponses])
    }
  }

  const onAudit = (item: LocalisationResponse) => {
    const prev = values.reponses.filter((r) => r.localisationId === item.id)

    const locs =
      form.answerMode === 'allSpaces'
        ? places.filter((pl) => form.localisationIds.includes(pl.id)).map((pl) => pl.libelle)
        : findLocalisationParents(places, item).map((l) => l.libelle)

    const responseTitle = [[societe ? societe.nameSociete : '', manif ? manif.libelle : ''], locs]
      .map((line) => line.filter(Boolean).join(' - '))
      .filter(Boolean)
      .join('\n')

    // open questions form
    if (bp === 'phone') {
      //open bottom drawer
      Drawer.open({
        render: () => (
          <Response
            questions={questions}
            reponses={prev}
            title={responseTitle}
            localisation={item.id}
            onResponse={(vals, partial) => {
              onReponse(item.id, vals)

              if (!partial) {
                Drawer.close()
              }
            }}
            onBack={Drawer.close}
          />
        ),
        fixed: true,
        backgroundColor: theme.colors.backgroundSecondary,
      })
    } else {
      SideDrawer.open({
        render: () => (
          <Response
            questions={questions}
            reponses={prev}
            title={responseTitle}
            localisation={item.id}
            onResponse={(vals, partial) => {
              onReponse(item.id, vals)
              if (!partial) {
                SideDrawer.close()
              }
            }}
            onBack={SideDrawer.close}
          />
        ),
        width: 600,
      })
    }
  }

  const renderLocation = (item: LocationTreeItem) => {
    const auditable = form.localisationIds.includes(item.location.id)
    const responses = values.reponses.filter((r) => r.localisationId === item.location.id)

    if (auditable) {
      return (
        <React.Fragment key={item.location.id}>
          <ItemHeader activeOpacity={0.9} disabled={questions.length === 0} onPress={() => onAudit(item.location)}>
            <ItemOpen>
              <ItemText>{item.location.libelle}</ItemText>
            </ItemOpen>

            <ItemResponseCount>{`${responses.length}/${questions.length}`}</ItemResponseCount>
          </ItemHeader>
        </React.Fragment>
      )
    }

    const open = opens.includes(item.location.id)
    const subs = item.subs.filter((sub) => filterInTree(sub, form.localisationIds))
    const toAudit = findAllAuditable(item, form.localisationIds)
    const progress =
      values.reponses.filter((r) => toAudit.includes(r.localisationId)).length / (toAudit.length * questions.length)

    return (
      <React.Fragment key={item.location.id}>
        <ItemHeader activeOpacity={0.9} onPress={() => onOpen(item.location.id, open)}>
          <ItemOpen>
            <ItemText>{item.location.libelle}</ItemText>
          </ItemOpen>

          <ItemProgress>
            <LinearProgress progress={progress} />
          </ItemProgress>
          <IconContainer open={open}>{subs.length > 0 && <Icon name="arrow-down" size={20} />}</IconContainer>
        </ItemHeader>
        {!!open && <Subs>{subs.map(renderLocation)}</Subs>}
      </React.Fragment>
    )
  }
  const renderAnswerAllSpacesCard = () => {
    const exampleItem = selectedLocationTree[0]
    const reponses = values.reponses.filter((r) => r.localisationId === exampleItem.location.id)

    return (
      <Card>
        <React.Fragment>
          <ItemHeader
            activeOpacity={0.9}
            disabled={questions.length === 0}
            onPress={() => onAudit(exampleItem.location)}>
            <ItemOpen>
              <ItemText>{selectedLocationTree.map((loc) => loc.location.libelle).join(', ')}</ItemText>
            </ItemOpen>

            <ItemResponseCount>{`${reponses.length}/${questions.length}`}</ItemResponseCount>
          </ItemHeader>
        </React.Fragment>
      </Card>
    )
  }

  const renderSpaceCard = ({ item }: { item: LocationTreeItem }) => {
    return <Card>{renderLocation(item)}</Card>
  }

  const columns = bp === 'phone' ? 1 : Math.floor((width - 300) / ITEM_WIDTH)

  return (
    <MainContainer>
      <Header>
        {bp !== 'phone' && (
          <TagContainer>
            <Tag
              activeColor
              focused
              item={{ label: i18n.t('screens.controles.form.quit'), value: 'tag' }}
              selected={false}
              leftIcon="close"
              onItemSelected={onQuit}
            />
          </TagContainer>
        )}

        <TitleContainer>
          {!!title && <Title>{title}</Title>}
          {!!subtitle && <SubTitle>{subtitle}</SubTitle>}
        </TitleContainer>
      </Header>

      <Info>
        {i18n.t(`screens.controles.form.questions.${notAudited.length === 0 ? 'completed' : 'info'}`, {
          count: notAudited.length,
        })}
      </Info>

      {form.answerMode === 'allSpaces' ? (
        renderAnswerAllSpacesCard()
      ) : (
        <FlatList
          key={columns}
          data={selectedLocationTree}
          keyExtractor={(item) => item.location.id}
          renderItem={renderSpaceCard}
          extraData={values}
          numColumns={columns}
        />
      )}
    </MainContainer>
  )
}

export default Questions

const MainContainer = styled(View)`
  flex: 1;
`

const Card = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  margin: 8px ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 8)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-left: 24px'};
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px`};
  ${(props) => props.theme.breakpoint !== 'phone' && 'align-self: flex-start;'};
  ${(props) => props.theme.constants.shadow};
`

const Subs = styled(View)`
  margin-left: 16px;
`

const ItemHeader = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: 1px;
`

const ItemOpen = styled(View)`
  flex: 1;
  padding-left: 16px;
`

const IconContainer = styled(View)<{ open: boolean }>`
  width: 30px;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.open ? 'rotate(0deg)' : 'rotate(-90deg)')};
`

const ItemText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const ItemResponseCount = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  padding: 0 16px;
`

const ItemProgress = styled(View)`
  width: 60px;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const Header = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primary : props.theme.colors.backgroundSecondary};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const TitleContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
  align-items: center;
  border-radius: 8px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  font-size: 22px;
  text-align: center;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const TagContainer = styled(View)`
  align-self: flex-start;
  margin-bottom: 24px;
`
